import * as go from 'gojs';
import * as React from 'react';
import { v4 as uuid } from 'uuid';
import { isDevelopment, isTesting } from 'vet-bones/utils';

import { INITIAL_LAYOUT_TRANSACTION_NAME } from 'src/containers/ProjectDiagram/constants/transactions';
import {
  hideContextMenu,
  showContextMenu,
} from 'src/containers/ProjectDiagram/handlers/contextMenu';
import { diagramChangedSelection } from 'src/containers/ProjectDiagram/handlers/diagram';
import {
  hideToolTip,
  showTooltip,
} from 'src/containers/ProjectDiagram/handlers/tooltip';
import { useDiagramObjectClick } from 'src/containers/ProjectDiagram/hooks/useDiagramObjectClick';
import { useDiagramObjectDoubleClick } from 'src/containers/ProjectDiagram/hooks/useDiagramObjectDoubleClick';
import { createDiagram } from 'src/containers/ProjectDiagram/templates/diagram';
import { ExtendedDiagram } from 'src/containers/ProjectDiagram/templates/extensions/diagram';
import { createModel } from 'src/containers/ProjectDiagram/templates/model';
import { ContextMenuData } from 'src/containers/ProjectDiagram/types/contextMenu';
import {
  ActiveDiagram,
  Diagram,
  DiagramLink,
  DiagramSnapshot,
  HeadlessDiagram,
} from 'src/containers/ProjectDiagram/types/diagram';
import { ModelData } from 'src/containers/ProjectDiagram/types/model';
import { TooltipData } from 'src/containers/ProjectDiagram/types/tooltip';
import { clearDiagramUndoHistory } from 'src/containers/ProjectDiagram/utils/clearDiagramUndoHistory';
import {
  DiagramModelCommitLayout,
  commitDiagramModel,
  commitDiagramWhileSkippingUndoManager,
} from 'src/containers/ProjectDiagram/utils/commitDiagramModel';
import { fixUnconnectedLinkPoints } from 'src/containers/ProjectDiagram/utils/fixUnconnectedLinkPoints';
import { rescaleDiagram } from 'src/containers/ProjectDiagram/utils/rescaleDiagram';
import { setDataProperty } from 'src/containers/ProjectDiagram/utils/setDataProperty';
import { setDiagramHighlight } from 'src/containers/ProjectDiagram/utils/setDiagramHighlight';
import { setDiagramSelection } from 'src/containers/ProjectDiagram/utils/setDiagramSelection';
import { shouldDispatchModelChange } from 'src/containers/ProjectDiagram/utils/shouldDispatchModelChange';
import { transformModelToSnapshot } from 'src/containers/ProjectDiagram/utils/transformModelToSnapshot';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

const isDev = isDevelopment();
const isTest = isTesting();

export const DiagramContext = React.createContext<ActiveDiagram | undefined>(
  undefined
);

/**
 * The active edit surface: a real `go.Diagram` (canvas built) or the headless
 * facade (large-model list view). Read `.model`/`.isReadOnly` directly on either
 * arm; narrow `!diagram.headless` before touching canvas-only members.
 */
export const useDiagram = () => {
  return React.useContext(DiagramContext);
};

type UseDiagramValueProps = {
  diagramSnapshot: DiagramSnapshot | null;
  handleDiagramUpdate: (snapshot: DiagramSnapshot) => void;
  handleDiagramSelection: (selection: go.Set<go.Part>) => void;
  headless: boolean;
  setContextMenu: (_: ContextMenuData | null) => void;
  setTooltip: (data: TooltipData | null) => void;
};

export const useDiagramValue = ({
  diagramSnapshot,
  handleDiagramUpdate,
  handleDiagramSelection,
  headless,
  setContextMenu,
  setTooltip,
}: UseDiagramValueProps) => {
  const { projectId } = useRouterParams();
  const processedIds = React.useRef<Set<string>>(new Set());
  const [activeValue, setActiveValue] = React.useState<
    ActiveDiagram | undefined
  >(undefined);

  const handleClick = useDiagramObjectClick();
  const handleDoubleClick = useDiagramObjectDoubleClick();

  // Detaches the listeners of whichever surface is active and cancels the frames
  // it has in flight. Both surfaces are built during render, so their listeners
  // outlive a rebuild unless something removes them: without this, an outgoing
  // diagram's deferred initial-layout frames still run against a detached div and
  // its model listener still dispatches, minting a snapshot id that rebuilds the
  // surface that just replaced it (VET-7007).
  const teardownActive = React.useRef<(() => void) | null>(null);

  React.useEffect(
    () => () => {
      teardownActive.current?.();
      teardownActive.current = null;
    },
    []
  );

  let currentActive = activeValue;

  const diagramSnapshotId = diagramSnapshot?.id;
  const isNewSnapshot =
    !!diagramSnapshotId && !processedIds.current.has(diagramSnapshotId);
  // Rebuild when the view flips between diagram and headless — the mode is
  // decided upstream (from the model-size threshold), so a build here is the
  // single place that owns "diagram or headless model".
  const modeChanged =
    currentActive !== undefined && currentActive.headless !== headless;

  if (diagramSnapshotId && headless && (isNewSnapshot || modeChanged)) {
    processedIds.current.clear();
    processedIds.current.add(diagramSnapshotId);

    teardownActive.current?.();
    teardownActive.current = null;

    // Switching a large model back to the headless list view: free the
    // previously-built diagram so the heavy GoJS tree doesn't linger. Only large
    // models reach headless, so only they pay the rebuild-from-snapshot cost on
    // re-opt-in (VET-7007). (ProjectDiagramContainer's effect cleanup still nulls
    // the div on full unmount — this handles the in-session mode flip.)
    if (currentActive && !currentActive.headless) {
      // GoJS has no dispose(): detaching the div releases the DOM element and
      // lets the tree be GC'd once React drops its reference; clearing the
      // dev/test global drops the last pin; and clearing the selection frees the
      // go.Parts that would otherwise keep the torn-down diagram alive.
      currentActive.div = null;
      if (isDev || isTest) {
        window.diagram = undefined;
      }
      handleDiagramSelection(new go.Set());
    }

    // A bare GraphLinksModel — no go.Diagram, no DOM — so the list view can
    // commit edits without paying for the visual tree. Mirrors changes back to
    // Redux with the same dispatch policy as the diagram listener below.
    const model = createModel(diagramSnapshot?.model);

    const handleHeadlessModelChanged = (e: go.ChangedEvent) => {
      if (shouldDispatchModelChange(e)) {
        const id = uuid();
        // Register before dispatching so the resulting snapshot doesn't rebuild
        // the model.
        processedIds.current.add(id);
        handleDiagramUpdate({ id, model: transformModelToSnapshot(e.model) });
      }
    };

    model.addChangedListener(handleHeadlessModelChanged);

    teardownActive.current = () => {
      model.removeChangedListener(handleHeadlessModelChanged);
    };

    const headlessDiagram: HeadlessDiagram = {
      headless: true,
      isReadOnly: false,
      model,
    };

    currentActive = headlessDiagram;
    setActiveValue(headlessDiagram);
  } else if (diagramSnapshotId && !headless && (isNewSnapshot || modeChanged)) {
    processedIds.current.clear();
    processedIds.current.add(diagramSnapshotId);

    teardownActive.current?.();
    teardownActive.current = null;

    const diagram = createDiagram({
      modelSnapshot: diagramSnapshot?.model,
      showContextMenu: showContextMenu(
        document.createElement('div'),
        setContextMenu
      ),
      hideContextMenu: hideContextMenu(setContextMenu),
      showToolTip: showTooltip(setTooltip),
      hideToolTip: hideToolTip(setTooltip),
      diagramChangedSelection: diagramChangedSelection((e) =>
        handleDiagramSelection(e.diagram.selection)
      ),
      handleClick,
      handleDoubleClick,
    });

    let hasClearedInitialUndoHistory = false;

    // The initial-layout work below defers to later frames. Track them so a
    // rebuild can cancel whatever is still queued — a frame that lands after the
    // diagram is torn down commits against a detached div.
    const pendingFrames = new Set<number>();
    const requestTrackedFrame = (callback: () => void) => {
      const frame = requestAnimationFrame(() => {
        pendingFrames.delete(frame);
        callback();
      });
      pendingFrames.add(frame);
    };

    const handleInitialLayoutCompleted = (e: go.DiagramEvent) => {
      if (
        e.diagram.layout.isInitial ||
        e.diagram.model.modelData.flaggedForRedraw
      ) {
        setDiagramSelection()(e.diagram as ExtendedDiagram);
        const hasExistingViewport = e.diagram.model.modelData.viewportBounds;
        if (!hasExistingViewport) {
          requestTrackedFrame(() => {
            commitDiagramWhileSkippingUndoManager(
              diagram,
              (_, diagram) => rescaleDiagram(diagram),
              INITIAL_LAYOUT_TRANSACTION_NAME
            );
          });
        }

        commitDiagramModel(
          e.diagram as Diagram,
          (model, diagram) => {
            model.linkDataArray.forEach((linkData) => {
              fixUnconnectedLinkPoints(linkData)(model, diagram);
            });

            setDataProperty<ModelData>(model)(
              model.modelData,
              'flaggedForRedraw',
              false
            );
          },
          {
            skipsCheck: true,
            layout: DiagramModelCommitLayout.FULL,
            tname: INITIAL_LAYOUT_TRANSACTION_NAME,
          }
        );

        // Force the diagram to update and redraw after invalidating routes
        // This ensures links are actually rendered.
        requestTrackedFrame(() => {
          commitDiagramWhileSkippingUndoManager(
            e.diagram as Diagram,
            (_model, diagram) => {
              // Force all links to invalidate routes again within the transaction
              diagram.links.each((link) => {
                const diagramLink = link as DiagramLink;
                diagramLink.invalidateRoute();
              });
            }
          );

          e.diagram.updateAllTargetBindings();
        });

        if (e.diagram.layout.isInitial && !hasClearedInitialUndoHistory) {
          hasClearedInitialUndoHistory = true;
          // InitialLayoutCompleted fires while the "Initial Layout" transaction
          // is still open, so defer the clear a frame — clearing the undo
          // manager mid-transaction leaves GoJS unable to end it.
          requestTrackedFrame(() => {
            clearDiagramUndoHistory(e.diagram as Diagram, projectId);
          });
        }
      }
    };

    const handleDiagramModelChanged = (e: go.ChangedEvent) => {
      if (
        e.model &&
        e.isTransactionFinished &&
        (e.propertyName === 'FinishedUndo' || e.propertyName === 'FinishedRedo')
      ) {
        diagram.commit(setDiagramHighlight());
        // undo/redo will restore diagram to previous viewstate while sometimes
        // ignoring current bindings (ex: fading with selection)
        diagram.updateAllTargetBindings('selection');
      }
      if (shouldDispatchModelChange(e)) {
        const id = uuid();
        processedIds.current.add(id);
        handleDiagramUpdate({ id, model: transformModelToSnapshot(e.model) });
      }
      if (
        e.model?.modelData.hiddenModelIds.length > 0 &&
        e.oldValue === 'Initial Layout'
      ) {
        diagram.updateAllTargetBindings('visible');
      }
    };

    diagram.addDiagramListener(
      'InitialLayoutCompleted',
      handleInitialLayoutCompleted
    );
    diagram.addModelChangedListener(handleDiagramModelChanged);

    teardownActive.current = () => {
      pendingFrames.forEach((frame) => cancelAnimationFrame(frame));
      pendingFrames.clear();
      diagram.removeDiagramListener(
        'InitialLayoutCompleted',
        handleInitialLayoutCompleted
      );
      diagram.removeModelChangedListener(handleDiagramModelChanged);
    };

    if (isDev || isTest) {
      window.diagram = diagram;
    }

    currentActive = diagram;
    setActiveValue(diagram);
  }

  return currentActive;
};
